import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import Select from 'react-windowed-select';

import { Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
	getOptionsFromInventoryItems,
	getProductCodesFromOrderProducts,
	getRentalOrderProductsQuery,
	getSalesOrderProductsQuery,
	getSingleInventoryItemsQuery,
	isRelevantOrderProduct,
	OptionType,
	sortOptions,
} from './utils';
import { OrderProduct, PurchaseTypes, SetProduct, Shopper } from 'common/types';

import { useSelector, useDispatch } from 'react-redux';
import { components } from 'components/AutoSuggest';
import { useTranslation } from 'services/localization';
import { getOrderProductSkuId } from 'common/modules/orders';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as RentalViewActions from 'actions/RentalViewActions';
import { useHasFeature, useQuery } from 'hooks';
import { isOrderProduct } from 'common/modules/orders/products/typeGuards';
import { switchUnreachable } from 'common/utils/common';
import { isEqual, uniqBy } from 'lodash';

export interface Props {
	productId: string;
	orderProduct: OrderProduct | SetProduct;
	parentProduct: OrderProduct | null;
	shopper: Shopper;
	setIndex: number | null;
	dataQA?: string;
	disabled?: boolean;
}

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
	return isEqual(prevProps, nextProps);
};

const ProductCodeInput = React.memo((props: Props) => {
	const {
		productId,
		orderProduct,
		parentProduct,
		shopper,
		setIndex,
		dataQA,
		disabled = false,
	} = props;

	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { hasFeature } = useHasFeature();
	const shopId = useSelector(ShopSelectors.activeShopId);
	const startLocationId = useSelector(RentalViewSelectors.orderStartLocationId);
	const ownOrderProducts = useSelector(RentalViewSelectors.orderProducts);

	const hideProductCodeErrors = useSelector(RentalViewSelectors.hideProductCodeErrors);

	const selectEl = useRef(null);
	const [inputValue, setInputValue] = useState('');

	const selectedCode = orderProduct?.productCode ?? '';

	const inputId = `react-select-single-${productId}-${setIndex != null ? setIndex : ''}`;

	const showItemCodesFromAllStores = hasFeature('SHOW_ITEM_CODES_FROM_ALL_STORES');
	const showRentalViewFormErrors = useSelector(ViewSelectors.showRentalViewFormErrors);

	const { startDate, endDate, skuId, purchaseType, locationId } = useMemo(() => {
		const now = moment.utc().toISOString();
		const purchaseType = orderProduct?.purchaseType ?? PurchaseTypes.rental;
		const skuId = orderProduct ? getOrderProductSkuId(orderProduct) : null;
		const startDate =
			(isOrderProduct(orderProduct) ? orderProduct?.startDate : parentProduct?.startDate) || now;
		const endDate =
			(isOrderProduct(orderProduct) ? orderProduct?.endDate : parentProduct?.endDate) || now;
		return {
			startDate,
			endDate,
			skuId,
			purchaseType,
			locationId: !showItemCodesFromAllStores && !!startLocationId ? startLocationId : null,
		};
	}, [orderProduct, parentProduct, showItemCodesFromAllStores, startLocationId]);

	const inventoryItemsQuery = useMemo(() => {
		if (!skuId || !shopId) return null;
		return getSingleInventoryItemsQuery({
			shopId,
			locationId,
			skuId,
		});
	}, [locationId, shopId, skuId]);

	const rentalOrderProductsQuery = useMemo(() => {
		if (!skuId) return null;
		return getRentalOrderProductsQuery({
			skuId,
			startDate,
		});
	}, [skuId, startDate]);

	const salesOrderProductsQuery = useMemo(() => {
		if (!skuId) return null;
		return getSalesOrderProductsQuery({
			skuId,
		});
	}, [skuId]);

	const rentalOrderProductsFilter = useCallback(
		(orderProduct: OrderProduct) => {
			return isRelevantOrderProduct({ startDate, endDate, purchaseType: 'rental' })(orderProduct);
		},
		[startDate, endDate],
	);

	const salesOrderProductsFilter = useCallback(
		(orderProduct: OrderProduct) => {
			return isRelevantOrderProduct({ startDate, endDate, purchaseType: 'sales' })(orderProduct);
		},
		[startDate, endDate],
	);

	const inventoryItems = useQuery({
		query: inventoryItemsQuery,
		disableLimit: true,
	});

	const rentalOrderProducts = useQuery({
		query: rentalOrderProductsQuery,
		disableLimit: true,
		queryFilter: rentalOrderProductsFilter,
	});

	const salesOrderProducts = useQuery({
		query: salesOrderProductsQuery,
		disableLimit: true,
		queryFilter: salesOrderProductsFilter,
	});

	const isLoading = inventoryItems.loading;
	const inventoryItemsLoaded = !!inventoryItems.data;

	const options = useMemo(() => {
		const allOrderProducts = uniqBy(
			[
				...(rentalOrderProducts.data ?? []),
				...(salesOrderProducts.data ?? []),
				...ownOrderProducts,
			],
			'id',
		);
		const productCodesInUse = getProductCodesFromOrderProducts({
			orderProducts: allOrderProducts,
			currentOrderProduct: {
				id: isOrderProduct(orderProduct) ? orderProduct.id : parentProduct?.id ?? '',
				setIndex,
			},
		});

		const options = getOptionsFromInventoryItems({
			inventoryItems: inventoryItems.data ?? [],
			productCodesInUse,
		});

		return sortOptions({
			selectedIdentifier: selectedCode,
			purchaseType,
		})(options);
	}, [
		rentalOrderProducts.data,
		salesOrderProducts.data,
		ownOrderProducts,
		inventoryItems.data,
		orderProduct,
		parentProduct,
		setIndex,
		selectedCode,
		purchaseType,
	]);

	const handleEnterPress = (event: any) => {
		switch (event.keyCode) {
			case 13: // ENTER
				event.preventDefault();
				if (selectEl && selectEl.current) {
					const selectedStockItem = (inventoryItems.data ?? []).find((item) =>
						item.identifiers.includes(inputValue),
					);
					dispatch(
						RentalViewActions.updateSelectedProductCode({
							code: inputValue.toUpperCase(),
							selectedStockItem,
							orderProduct,
							orderProductId: productId,
							shopper,
							setIndex,
							t,
						}),
					);
					(selectEl.current as any).blur();
				}
				break;
			default:
				break;
		}
	};

	const handleOptionSelect = (option: OptionType | null) => {
		setInputValue('');
		const selectedStockItem = (inventoryItems.data ?? []).find(
			(item) => item.id === option?.inventoryItemId,
		);
		dispatch(
			RentalViewActions.updateSelectedProductCode({
				code: option?.value.toUpperCase() ?? '',
				selectedStockItem,
				orderProduct,
				orderProductId: productId,
				shopper,
				setIndex,
				t,
			}),
		);
	};

	const selectedOption = options.find((o) => o.value === selectedCode);
	const showErrors = !!selectedCode && !hideProductCodeErrors;

	const idNotFound = showErrors && inventoryItemsLoaded && !selectedOption;
	const idOccupied = showErrors && !!selectedOption?.isOccupied;
	const idOutOfUse = showErrors && selectedOption?.status === 'OUT_OF_USE';
	const idWrongPurchaseType =
		showErrors && inventoryItemsLoaded && !selectedOption?.allocationTypes?.includes(purchaseType);

	const errorText = (() => {
		if (props.disabled || !selectedCode) {
			return null;
		}
		if (idNotFound) return t('idIsNotInTheInventory', 'Code is not in the inventory');
		if (idOutOfUse) return t('idOutOfUse', 'Product code is marked as out of use');
		if (idOccupied) return t('idInUse', 'Product code is already in use');

		if (idWrongPurchaseType) {
			switch (purchaseType) {
				case PurchaseTypes.rental:
					return t('idNotForRent', 'Product code is not for rent');
				case PurchaseTypes.sales:
					return t('idNotForSale', 'Product code is not for sale');
				default:
					return switchUnreachable(purchaseType);
			}
		}

		return null;
	})();

	return (
		<div>
			<Select
				ref={selectEl}
				classes={classes}
				error={showRentalViewFormErrors && !selectedCode}
				value={{
					label: selectedCode,
					value: selectedCode,
				}}
				options={options}
				menuPlacement="auto"
				onKeyDown={handleEnterPress}
				isDisabled={disabled}
				occupied={idOccupied}
				outOfUse={idOutOfUse}
				wrongPurchaseType={idWrongPurchaseType}
				purchaseType={purchaseType}
				isLoading={isLoading}
				openMenuOnFocus
				isClearable
				maxMenuHeight={200}
				inputId={inputId}
				TextFieldProps={{
					InputLabelProps: {
						htmlFor: inputId,
						shrink: true,
						classes: { root: classes.labelRoot },
					},
					'data-qa': dataQA,
				}}
				onChange={handleOptionSelect}
				onInputChange={(inputValue: any, { action }: any) => {
					if (action === 'input-change') {
						setInputValue((inputValue || '').toUpperCase());
					}
				}}
				styles={{
					input: (base: any) => ({
						...base,
						font: 'inherit',
					}),
					group: (base: any) => ({
						...base,
						padding: '4px 0px',
					}),
					option: (base: any) => ({
						...base,
						height: 48,
					}),
				}}
				components={components}
				windowThreshold={50}
			/>
			<Typography
				variant="caption"
				color="textSecondary"
				className={classes.identifierCaption}
				data-qa="inventoryError"
				sx={{
					'&:empty': {
						display: 'none',
					},
				}}
			>
				{errorText}
			</Typography>
		</div>
	);
}, propsAreEqual);

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		identifierCaption: {
			lineHeight: 'normal',
		},
		labelRoot: {
			marginTop: -4,
		},
		input: {
			display: 'flex',
			height: 32,
			padding: '4px 6px',
			fontSize: '1.4rem !important',
			lineHeight: 'normal',
			overflow: 'hidden',
		},
		noOptionsMessage: {
			padding: theme.spacing(1, 2),
		},
		valueWrapper: {
			display: 'flex',
			alignItems: 'center',
			flexWrap: 'nowrap',
			flex: 1,
			'& div': {
				display: 'flex',
				flex: 1,
			},
		},
		valueContainer: {
			padding: '0',
			fontSize: '1.4rem !important',
			lineHeight: 'normal',
			overflowX: 'scroll',
			overflowY: 'hidden',
			'scrollbar-width': 'none' /* Firefox */,
			'scrollbar-height': 'none',
			'-ms-overflow-style': 'none' /* IE 10+ */,
			marginBottom: -20,
			paddingBottom: 20,
			'&::-webkit-scrollbar': {
				width: 0,
				height: 0,
			},
			flexWrap: 'nowrap',
			display: 'flex',
			alignItems: 'center',
			flex: 1,
		},
		chip: {
			minWidth: 32,
			flex: 'initial !important',
		},
		warningBg: {
			backgroundColor: theme.palette.warning.main,
		},
		errorBg: {
			backgroundColor: theme.palette.error.main,
		},
		disabledBg: {
			backgroundColor: theme.palette.action.disabled,
		},
		chipLabel: {
			paddingLeft: 6,
			paddingRight: 6,
			fontSize: '1.2rem',
		},
		placeholder: {
			position: 'absolute',
			left: 2,
			bottom: 6,
		},
		activeHeader: {
			color: theme.palette.primary.main,
			fontWeight: 500,
		},
		paper: {
			position: 'absolute',
			zIndex: 3,
			marginTop: theme.spacing(1),
			left: 0,
			width: 'auto',
			minWidth: '100%',
		},
		divider: {
			height: theme.spacing(2),
		},
		groupHeading: {
			padding: theme.spacing(0.5, 1),
		},
		primaryOption: {
			width: 'auto',
			flex: 1,
		},
		informationOption: {
			flex: 1,
			textAlign: 'right',
			fontWeight: 500,
			paddingLeft: theme.spacing(2),
		},
		warningColor: {
			color: theme.palette.warning.main,
		},
		errorColor: {
			color: theme.palette.error.main,
		},
	}),
);

export default ProductCodeInput;
